<template>
    <div class="history-entry-row">
        <span class="roster-name">{{ rosterName }}</span>
        <div class="shift-change">
            <span class="label" v-if="changeSourceLabel">{{ changeSourceLabel }}:</span>
            <span class="shift-label">{{ shiftLabel }}</span>
            <span class="shift-comment" v-if="historySetEntry.deployment.publicComment">{{ historySetEntry.deployment.publicComment }}</span>
        </div>
        <div class="specifications" :class="{'with-projects': hasProjectSpecifications}" v-if="historySetEntry.deployment.specifications && historySetEntry.deployment.specifications.length > 0">
            <div class="specification-header">
                <div>Van - Tot
                    <span v-if="historySetEntry.deployment.specified"> (Gespecificeerd)</span>
                </div>
                <div>Soort</div>
                <div>Toestand</div>
                <div class="projects">Project</div>
                <div class="activities">Activiteit</div>
            </div>
            <div
                v-for="(specification, index) in historySetEntry.deployment.specifications ?? []"
                :key="'specification|' + index"
                class="specification"
                :class="{specified: historySetEntry.deployment.specified}"
            >
                <div>{{ formatTimePeriod(specification.begin, specification.end) }}</div>
                <div>{{ organisationDeploymentTypes[specification.typeId].name }}</div>
                <div>{{ organisationDeploymentStates[specification.stateId].caption }}</div>
                <div v-if="hasProjectSpecifications" class="projects">
                    {{ getProjectDescription(specification.projectId!) }}
                </div>
                <div v-if="hasProjectSpecifications" class="activities">
                    {{ getProjectActivityDescription(specification.projectId!, specification.activityId) }}
                </div>
            </div>
        </div>
        <div v-else class="no-specifications">
            Geen inzet
        </div>
        <div class="edit-on-published-roster">
            <span v-if="historySetEntry.editOnPublishedRoster">Ja</span>
            <span v-else>Nee</span>
        </div>
    </div>
</template>

<script lang="ts">
    import Dates from '@cquential/date-utils';
    import {OrganisationDeploymentState, DeploymentType, Shift, Project, DeploymentSpecification, Activity, DeploymentChangeSource} from '@cquential/api';
    import {DeploymentHistorySetEntry} from '@/components/modals/types';
    import {Component, Prop, Vue} from 'vue-facing-decorator';

    @Component
    export default class HistoryEntry extends Vue {
        @Prop({required: true}) public historySetEntry!: DeploymentHistorySetEntry;
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop({required: true}) public organisationDeploymentTypes!: Record<number, DeploymentType>;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public rosterNames!: Record<number, string>;
        @Prop() public projects?: Project[];

        public get shiftLabel(): string {
            if (this.organisationDeploymentStates[this.historySetEntry.deployment.stateId] && this.organisationDeploymentStates[this.historySetEntry.deployment.stateId].code !== null) {
                return this.organisationDeploymentStates[this.historySetEntry.deployment.stateId].code!;
            }

            if (this.shifts[this.historySetEntry.deployment.shiftId]) {
                return this.shifts[this.historySetEntry.deployment.shiftId].template!.code;
            }

            return '';
        }

        public get rosterName(): string {
            return this.rosterNames[this.shifts[this.historySetEntry.deployment.shiftId].rosterId];
        }

        public get hasProjectSpecifications(): boolean {
            return this.historySetEntry.deployment.specifications?.some((specification: DeploymentSpecification): boolean => {
                return !!specification.projectId;
            }) ?? false;
        }

        public get changeSourceLabel(): string | null {
            switch (this.historySetEntry.changeSource) {
                case DeploymentChangeSource.STAMP:
                    return 'Aangemaakt uit stempel';
                case DeploymentChangeSource.PUBLISH:
                    return 'Gepubliceerd';
                case DeploymentChangeSource.AUTOMATIC_SCHEDULING_BY_EMPLOYEE:
                    return 'Gewijzigd d.m.v. automatisch roosteren';
                case DeploymentChangeSource.TRADE:
                    return 'Gewijzigd d.m.v. ruiling';
            }

            return null;
        }

        public getProjectDescription(projectId: number): string {
            const project: Project | undefined = this.projects?.find((project: Project): boolean => project.id === projectId);

            if (project) {
                return project.id + ' - ' + project.description;
            }

            return '';
        }

        public getProjectActivityDescription(projectId: number, activityId: number | null): string {
            const activity: Activity | undefined = this.projects?.find((project: Project): boolean => project.id === projectId)
                ?.activities?.find((activity: Activity): boolean => activity.id === activityId);

            if (activity) {
                return activity.id + ' - ' + activity.description;
            }

            return '';
        }

        public formatTimePeriod(begin: Date, end: Date): string {
            let addition: string = '';
            if (Dates.Utils.diffInDays(end, begin) > 0) {
                addition = '+1 ';
            }

            return Dates.Formatter.formatTime(begin) + ' - ' + addition + Dates.Formatter.formatTime(end);
        }
    }
</script>

<style lang="scss" scoped>
    .roster-name,
    .shift-change,
    .edit-on-published-roster,
    .specifications,
    .no-specifications {
        padding: .25rem .5rem;
        border-bottom: 1px solid lightgrey;
    }

    .roster-name {
        grid-column: 3;
    }

    .shift-change {
        .label {
            margin-right: .25rem;
        }

        .shift-label {
            font-style: italic;
        }

        .shift-comment {
            margin-left: .25rem;
            font-weight: bold;
            font-style: italic;
        }
    }

    .specifications {
        display: grid;
        grid-template-columns: repeat(3, 1fr);

        &.with-projects {
            grid-template-columns: repeat(5, 1fr);

            .projects,
            .activities {
                display: block;
            }
        }

        .projects,
        .activities {
            display: none;
        }

        .specification,
        .specification-header {
            display: contents;
        }

        .specification-header {
            font-weight: bold;
        }
    }

    .history-entry-row {
        display: contents;
    }

    :nth-last-child(-n+1 of .history-entry-row) > * {
        border-bottom: 0;
    }
</style>
